import { Fragment, useState } from 'react';
import { Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '../../../../componentsv2/Typography';
import Button from '../../../../componentsv2/Button';
import TwoFactorAuthInput from '../../../../componentsv2/TwoFactorAuthInput';
import { useTranslation } from '../../../../hooks/i18n';
import { Nullable } from '../../../../types';
import { COLORS } from '../../../../themev2';

interface Step1TwoFactorCodeProps {
  onContinue: (code: string) => Promise<void>;
  onChooseDifferent: () => void;
  rememberDevice: boolean;
  setRememberDevice: (value: boolean) => void;
  error: Nullable<string>;
  setError: (error: Nullable<string>) => void;
  isLoading: boolean;
}

const Step1TwoFactorCode = ({
  onContinue,
  onChooseDifferent,
  rememberDevice,
  setRememberDevice,
  error,
  setError,
  isLoading,
}: Step1TwoFactorCodeProps) => {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);

  const handleSubmit = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError(t('common:twoFactor.invalidCodeError'));
      return;
    }

    await onContinue(code);
  };

  return (
    <Fragment>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {t('common:twoFactor.title')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: COLORS.gray[1100],
              size: '18px',
              fontWeight: 400,
            }}
          >
            <Trans
              t={t}
              i18nKey={'common:twoFactor.description'}
              components={{
                span: <span style={{ color: COLORS.indigo[500], fontWeight: 600 }}></span>,
              }}
            >
              Please enter your 6-digit authentication code generated by the{' '}
              <span>Google Authenticator app</span>
            </Trans>
          </Typography>
        </Grid>
        <TwoFactorAuthInput
          onSubmit={handleSubmit}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          error={error}
          onError={setError}
        />
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: { xs: '100%', lg: '80%' },
            }}
          >
            <Button
              fullWidth
              color="secondary"
              disabled={!verificationCode.every((digit) => digit !== '')}
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              {t('common:twoFactor.continueButton')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                sx={{
                  '&.Mui-checked': {
                    color: COLORS.indigo[500],
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: COLORS.gray[1100] }}>
                  {t('common:twoFactor.rememberDevice')}
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '2rem',
            }}
          >
            <Button fullWidth type="link" color="secondary" onClick={onChooseDifferent}>
              {t('common:twoFactor.chooseDifferentMethod')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default Step1TwoFactorCode;
